import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { formatInTimeZone } from "date-fns-tz";
import {
  CalendarClock,
  ArrowRight,
  Quote,
  Sparkles,
  Users,
  MapPin,
  ChevronRight,
  CalendarRange,
} from "lucide-react";
import {
  getAllBookings,
  getQuoteRequests,
  getAllGlamPackages,
} from "../../services/api";
import {
  HALIFAX_TZ,
  toHalifaxDateString,
  formatSlotTime,
  formatSlotDate,
} from "../../lib/timezone";
import { StatusBadge } from "../../components/dashboard/shop/status-badge";
import { ToastProvider } from "../../components/providers/toast-provider";

interface Booking {
  id: string;
  name?: string;
  booking_name?: string;
  email_address?: string;
  phone_number?: string;
  glam_package_id?: string;
  glam_spot?: string;
  number_of_faces?: number;
  booking_start_date_time?: string;
  status: "scheduled" | "completed" | "declined";
}

const Dashboard = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [packages, setPackages] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([getAllBookings(), getQuoteRequests(), getAllGlamPackages()])
      .then(([b, q, p]: any) => {
        if (!active) return;
        setBookings(b?.data?.data ?? []);
        setQuotes(q?.data?.data ?? []);
        setPackages(p?.data?.data ?? []);
      })
      .catch(() => toast.error("Could not load your dashboard."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const now = new Date();
  const todayIso = toHalifaxDateString(now);
  const greeting = useMemo(() => {
    const hour = Number(formatInTimeZone(now, HALIFAX_TZ, "H"));
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  }, [now]);

  const packageTitle = (id?: string) =>
    packages.find((p) => p.id === id)?.title ?? "Session";

  const { today, upcoming, weekCount } = useMemo(() => {
    const nowMs = now.getTime();
    const weekEndMs = nowMs + 7 * 86400000;
    const active = bookings.filter((b) => b.status !== "declined" && b.booking_start_date_time);
    const sorted = [...active].sort(
      (a, b) =>
        new Date(a.booking_start_date_time!).getTime() -
        new Date(b.booking_start_date_time!).getTime()
    );
    const today = sorted.filter(
      (b) => toHalifaxDateString(b.booking_start_date_time!) === todayIso
    );
    const upcoming = sorted.filter(
      (b) =>
        new Date(b.booking_start_date_time!).getTime() > nowMs &&
        toHalifaxDateString(b.booking_start_date_time!) !== todayIso
    );
    const weekCount = sorted.filter((b) => {
      const t = new Date(b.booking_start_date_time!).getTime();
      return t >= nowMs && t <= weekEndMs;
    }).length;
    return { today, upcoming, weekCount };
  }, [bookings, todayIso, now]);

  const customerName = (b: Booking) => b.name || b.booking_name || "Client";

  if (loading) {
    return (
      <>
        <ToastProvider />
        <div className="flex items-center gap-3 py-20 text-[#9A6B69]">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#EAD7D6] border-t-[#B87775]" />
          Loading your day…
        </div>
      </>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      <ToastProvider />
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">
          {formatInTimeZone(now, HALIFAX_TZ, "EEEE, d MMMM yyyy")} · Atlantic Time
        </p>
        <h1 className="font-serif text-3xl text-[#3a2e2c]">{greeting}.</h1>
        <p className="mt-1 text-sm text-[#8a7a78]">
          {today.length > 0
            ? `You have ${today.length} booking${today.length > 1 ? "s" : ""} today.`
            : "Nothing booked today — a good chance to get ahead."}
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Today + What's next */}
        <div className="space-y-5 lg:col-span-2">
          <section className="rounded-2xl border border-[#EAD7D6] bg-white p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-serif text-xl text-[#3a2e2c]">
                <CalendarClock className="h-5 w-5 text-[#B87775]" />
                Today's schedule
              </h2>
              <Link
                to="/control/bookings"
                className="flex items-center gap-1 text-sm text-[#B87775] hover:underline"
              >
                All bookings <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {today.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-[#E3C9C8] bg-[#FFFBFB] px-5 py-10 text-center">
                <Sparkles className="mx-auto h-8 w-8 text-[#D9C4C3]" />
                <p className="mt-2 text-sm text-[#8a7a78]">
                  No appointments today. Add one manually or block the day off in your
                  schedule.
                </p>
                <div className="mt-4 flex justify-center gap-2">
                  <Link
                    to="/control/bookings/add"
                    className="rounded-full bg-[#B87775] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#a96764]"
                  >
                    Add booking
                  </Link>
                  <Link
                    to="/control/schedule"
                    className="rounded-full border border-[#E3C9C8] px-4 py-2 text-sm font-medium text-[#B87775] transition hover:bg-[#FBEFEF]"
                  >
                    Open schedule
                  </Link>
                </div>
              </div>
            ) : (
              <ul className="mt-4 space-y-2.5">
                {today.map((b) => (
                  <li key={b.id}>
                    <Link
                      to={`/control/bookings/view/${b.id}`}
                      className="group flex items-center gap-4 rounded-xl border border-[#EAD7D6] bg-[#FFFBFB] p-3.5 transition hover:border-[#B87775]"
                    >
                      <div className="flex w-20 shrink-0 flex-col items-center rounded-lg bg-[#FBEFEF] py-2">
                        <span className="font-serif text-lg leading-none tabular-nums text-[#B87775]">
                          {b.booking_start_date_time &&
                            formatInTimeZone(b.booking_start_date_time, HALIFAX_TZ, "h:mm")}
                        </span>
                        <span className="text-[10px] uppercase text-[#9A6B69]">
                          {b.booking_start_date_time &&
                            formatInTimeZone(b.booking_start_date_time, HALIFAX_TZ, "a")}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-[#3a2e2c]">{customerName(b)}</p>
                        <p className="truncate text-sm text-[#8a7a78]">
                          {packageTitle(b.glam_package_id)}
                          {b.glam_spot && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[#9A6B69]">
                              <MapPin className="h-3 w-3" />
                              {b.glam_spot.replace(/_/g, " ")}
                            </span>
                          )}
                        </p>
                      </div>
                      <StatusBadge status={b.status} />
                      <ChevronRight className="h-4 w-4 shrink-0 text-[#D9C4C3] transition group-hover:text-[#B87775]" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* What's next */}
          <section className="rounded-2xl border border-[#EAD7D6] bg-white p-5 sm:p-6">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[#3a2e2c]">
              <CalendarRange className="h-5 w-5 text-[#B87775]" />
              Coming up
            </h2>
            {upcoming.length === 0 ? (
              <p className="text-sm text-[#8a7a78]">No upcoming bookings on the books yet.</p>
            ) : (
              <ul className="divide-y divide-[#F0E2E1]">
                {upcoming.slice(0, 5).map((b) => (
                  <li key={b.id}>
                    <Link
                      to={`/control/bookings/view/${b.id}`}
                      className="flex items-center gap-4 py-3 transition hover:opacity-80"
                    >
                      <div className="w-32 shrink-0 text-sm tabular-nums text-[#8a5a58]">
                        {b.booking_start_date_time && formatSlotDate(b.booking_start_date_time)}
                      </div>
                      <div className="w-24 shrink-0 text-sm tabular-nums text-[#9A6B69]">
                        {b.booking_start_date_time && formatSlotTime(b.booking_start_date_time).replace(/ (ADT|AST)$/, "")}
                      </div>
                      <p className="min-w-0 flex-1 truncate text-[#3a2e2c]">{customerName(b)}</p>
                      <span className="hidden truncate text-sm text-[#8a7a78] sm:block">
                        {packageTitle(b.glam_package_id)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Needs attention + week glance */}
        <div className="space-y-5">
          <section className="rounded-2xl border border-[#EAD7D6] bg-white p-5 sm:p-6">
            <h2 className="mb-1 font-serif text-xl text-[#3a2e2c]">Needs your attention</h2>
            <p className="mb-4 text-sm text-[#8a7a78]">Respond to keep your pipeline moving.</p>

            <Link
              to="/control/quotes"
              className="flex items-center gap-3 rounded-xl border border-[#EAD7D6] bg-[#FBEFEF]/50 p-4 transition hover:border-[#B87775]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FBEFEF] text-[#B87775]">
                <Quote className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <p className="font-serif text-2xl leading-none tabular-nums text-[#3a2e2c]">
                  {quotes.length}
                </p>
                <p className="text-sm text-[#8a7a78]">
                  Quote request{quotes.length === 1 ? "" : "s"} to review
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-[#B87775]" />
            </Link>
          </section>

          <section className="rounded-2xl border border-[#EAD7D6] bg-white p-5 sm:p-6">
            <h2 className="mb-4 font-serif text-xl text-[#3a2e2c]">This week</h2>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Bookings next 7 days" value={weekCount} icon={CalendarClock} />
              <Stat label="Today" value={today.length} icon={Sparkles} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link
                to="/control/schedule"
                className="flex items-center justify-center gap-1.5 rounded-full border border-[#E3C9C8] px-3 py-2.5 text-sm font-medium text-[#B87775] transition hover:bg-[#FBEFEF]"
              >
                <CalendarRange className="h-4 w-4" /> Schedule
              </Link>
              <Link
                to="/control/customers"
                className="flex items-center justify-center gap-1.5 rounded-full border border-[#E3C9C8] px-3 py-2.5 text-sm font-medium text-[#B87775] transition hover:bg-[#FBEFEF]"
              >
                <Users className="h-4 w-4" /> Customers
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

function Stat({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-xl border border-[#EAD7D6] bg-[#FFFBFB] p-3.5">
      <Icon className="h-4 w-4 text-[#B87775]" />
      <p className="mt-2 font-serif text-3xl leading-none tabular-nums text-[#3a2e2c]">{value}</p>
      <p className="mt-1 text-xs text-[#9A6B69]">{label}</p>
    </div>
  );
}

export default Dashboard;
